import React, { useState } from 'react';
import { passwordResetService } from '../services/passwordResetService';
import { validateField } from '../utils/formValidation';
import styles from '../styles/components/PasswordResetForm.styles';

const PasswordResetForm = ({ onResetSuccess, onBackToLogin }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  /**
   * パスワードリセット実行処理
   */
  const executePasswordReset = async (email) => {
    const trimmedEmail = email.trim();
    
    try {
      setLoading(true);
      setError('');
      setMessage('');

      // バックエンドAPIを呼び出し
      const result = await passwordResetService.sendPasswordResetEmail(trimmedEmail);
      
      if (result.success) {
        setEmailSent(true);
        setMessage(`${trimmedEmail} にパスワードリセット用のメールを送信しました。\nメールをご確認の上、記載されているリンクをクリックしてパスワードをリセットしてください。`);

        // 成功時のコールバック実行
        if (onResetSuccess) {
          onResetSuccess();
        }
      } else {
        setError(result.error || 'パスワードリセットに失敗しました');
        setEmailSent(false);
      }

    } catch (error) {
      console.error('[PASSWORD RESET] Reset failed:', error);
      setError(error.message || 'パスワードリセットに失敗しました');
      setEmailSent(false);
    } finally {
      setLoading(false);
    }
  };

  /**
   * フォーム送信処理
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 重複送信防止
    if (loading) return;
    
    // プロジェクト統一のバリデーション関数を使用
    const validationError = validateField('email', email);
    if (validationError) {
      setError(validationError);
      return;
    }

    // パスワードリセット実行
    await executePasswordReset(email);
  };

  /**
   * 入力フィールドの変更処理
   */
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    
    // リアルタイムバリデーション実行
    if (value.trim()) {
      const validationError = validateField('email', value);
      setError(validationError || '');
    } else {
      // 入力が空の場合はエラーをクリア
      setError('');
    }
  };

  /**
   * フォームリセット処理
   */
  const handleReset = () => {
    setEmail('');
    setMessage('');
    setError('');
    setEmailSent(false);
  };
  
  /**
   * ログイン画面に戻る処理
   */
  const handleBackToLoginClick = () => {
    if (onBackToLogin) {
      onBackToLogin();
    } else {
      // フォールバック：直接ナビゲート
      window.location.href = '/login';
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.form}>
        <div style={styles.header}>
          <h2 style={styles.title}>パスワードをリセット</h2>
          {!emailSent ? (
            <p style={styles.description}>
              <span>登録されたメールアドレスを入力してください。</span><br />
              <span>パスワードリセット用のリンクを送信いたします。</span>
            </p>
          ) : (
            <p style={{...styles.description, ...styles.successDescription}}>
              メールを送信しました！
            </p>
          )}
        </div>

        {!emailSent ? (
          <form onSubmit={handleSubmit} style={styles.resetForm}>
            <div style={styles.formGroup}>
              <label htmlFor="email" style={styles.label}>
                <span>メールアドレス</span><span style={styles.required}> *</span>
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={handleEmailChange}
                disabled={loading}
                placeholder="example@email.com"
                style={{
                  ...styles.input,
                  ...(error ? styles.inputError : {}),
                  ...(loading ? styles.inputDisabled : {})
                }}
              />
              {error && (
                <div style={styles.errorMessage} role="alert">
                  {error}
                </div>
              )}
            </div>

            <div style={styles.formActions}>
              <button
                type="submit"
                disabled={loading || !email.trim()}
                style={{
                  ...styles.submitButton,
                  ...styles.primaryButton,
                  ...(loading || !email.trim() ? styles.disabledButton : {})
                }}
                onMouseOver={(e) => {
                  if (!loading && email.trim()) {
                    Object.assign(e.currentTarget.style, styles.primaryButtonHover);
                  }
                }}
                onMouseOut={(e) => {
                  if (!loading && email.trim()) {
                    Object.assign(e.currentTarget.style, styles.primaryButton);
                  }
                }}
              >
                {loading ? (
                  <>
                    <span style={styles.loadingSpinner}></span>
                    <span>送信中...</span>
                  </>
                ) : (
                  <span>リセットメールを送信</span>
                )}
              </button>
            </div>
          </form>
        ) : (
          <div style={styles.successContent}>
            {message && (
              <div style={styles.successMessage} role="alert">
                <span style={styles.messageIcon}>✅</span>
                <div style={styles.messageText}>
                  {message.split('\n').map((line, index) => (
                    <div key={index}>{line}</div>
                  ))}
                </div>
              </div>
            )}

            <div style={styles.successInfo}>
              <h3 style={styles.successInfoTitle}>次の手順</h3>
              <ol style={styles.successInfoList}>
                <li style={styles.successInfoItem}>メールボックスを確認してください</li>
                <li style={styles.successInfoItem}>「パスワードリセット」の件名のメールを探してください</li>
                <li style={styles.successInfoItem}>メール内のリンクをクリックしてください</li>
                <li style={styles.successInfoItem}>新しいパスワードを設定してください</li>
              </ol>
              
              <div style={styles.infoNote}>
                <p style={styles.infoNoteText}>
                  <strong>メールが届かない場合：</strong><br />
                  <span>• 入力したメールアドレスが正しいかご確認ください</span><br />
                  <span>• アカウントが登録されていない可能性があります</span><br />
                  <span>• 迷惑メールフォルダをご確認ください</span><br />
                  <span>• しばらく待ってから再度お試しください</span>
                </p>
              </div>
            </div>

            <div style={styles.formActions}>
              <button 
                type="button"
                onClick={handleReset}
                style={{...styles.submitButton, ...styles.secondaryButton}}
                onMouseOver={(e) => {
                  Object.assign(e.target.style, styles.secondaryButtonHover);
                }}
                onMouseOut={(e) => {
                  Object.assign(e.target.style, styles.secondaryButton);
                }}
              >
                別のメールアドレスで再送信
              </button>
            </div>
          </div>
        )}

        <div style={styles.footer}>
          <div style={styles.footerLinks}>
            <button 
              type="button"
              onClick={handleBackToLoginClick}
              style={{
                ...styles.footerLink,
                background: 'none',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              ← ログインページに戻る
            </button>
            <a href="/terms" style={styles.footerLink}>
              新規登録はこちら
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetForm;